import type { Bilingual, Formality, Lang, Room, Task, TaskResult, Weekday, WorkerLang } from './types';

/** Totals are always calculated from the tasks; nobody types a total (spec, "Rules the mockup leaves open"). */
type TaskLike = { id: string; roomId: string; days: Weekday[]; minutes: number; order: number };
type RoomLike = { id: string; order: number };

export function tasksForDay<T extends TaskLike = Task>(day: Weekday, all: T[]): T[] {
  return all.filter((tk) => tk.days.includes(day));
}

/**
 * The tasks on a date: its plan from the service (the routine with any one-time day moves applied)
 * when there is one, otherwise the weekly routine for that weekday.
 */
export function tasksInPlan<T extends TaskLike = Task>(plan: string[] | undefined, day: Weekday, all: T[]): T[] {
  if (!plan) return tasksForDay(day, all);
  const ids = new Set(plan);
  return all.filter((tk) => ids.has(tk.id));
}

/** "2026-10-27" for a local calendar date. */
export function isoOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export interface RoomGroup<R extends RoomLike = Room, T extends TaskLike = Task> {
  room: R;
  tasks: T[];
  minutes: number;
}

export function groupByRoom<R extends RoomLike = Room, T extends TaskLike = Task>(dayTasks: T[], allRooms: R[]): RoomGroup<R, T>[] {
  return [...allRooms]
    .sort((a, b) => a.order - b.order)
    .map((room) => {
      const list = dayTasks.filter((tk) => tk.roomId === room.id).sort((a, b) => a.order - b.order);
      return { room, tasks: list, minutes: sumMinutes(list) };
    })
    .filter((g) => g.tasks.length > 0);
}

export function sumMinutes(list: { minutes: number }[]): number {
  return list.reduce((n, tk) => n + tk.minutes, 0);
}

/** 195 -> "3 h 15 min"; 45 -> "45 min". */
export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** Text for the reader's language. Staff always get Spanish, in their household's "tú" or "usted". */
export function pick(text: Bilingual, lang: Lang | WorkerLang, formality: Formality = 'tu'): string {
  // An empty side means the translation is still pending; fall back to the original.
  if (lang === 'en') return text.en || text.es;
  if (lang === 'es') return (formality === 'usted' && text.esUsted ? text.esUsted : text.es) || text.en;
  return (text as unknown as Record<string, string | undefined>)[lang] || text.en || text.es;
}

/** True when the reader's language has no translation yet. */
export function translationPending(text: Bilingual, lang: Lang): boolean {
  return !text[lang] && !!(text.en || text.es);
}

const WEEKDAYS: Record<Lang, string[]> = {
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  es: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
};
const WEEKDAYS_SHORT: Record<Lang, string[]> = {
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  es: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
};
const MONTHS: Record<Lang, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  es: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
};

export function weekdayName(day: Weekday, lang: Lang): string {
  return WEEKDAYS[lang][day];
}
export function weekdayShort(day: Weekday, lang: Lang): string {
  return WEEKDAYS_SHORT[lang][day];
}

/** Parses "2026-10-03" as a local calendar date. */
export function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function weekdayOf(date: Date): Weekday {
  return ((date.getDay() + 6) % 7) as Weekday;
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** "Sábado, 3 de octubre" / "Saturday, October 3". */
export function formatLongDate(date: Date, lang: Lang): string {
  const wd = WEEKDAYS[lang][weekdayOf(date)];
  const month = MONTHS[lang][date.getMonth()];
  return lang === 'es' ? `${wd}, ${date.getDate()} de ${month}` : `${wd}, ${month} ${date.getDate()}`;
}

export function formatShortDate(date: Date, lang: Lang): string {
  const wd = WEEKDAYS_SHORT[lang][weekdayOf(date)];
  const month = MONTHS[lang][date.getMonth()];
  return lang === 'es' ? `${wd} ${date.getDate()} ${month.slice(0, 3)}` : `${wd}, ${month.slice(0, 3)} ${date.getDate()}`;
}

/** For a sentence: "martes 27 de octubre" / "Tuesday, October 27". */
export function formatDayInSentence(date: Date, lang: Lang): string {
  const wd = WEEKDAYS[lang][weekdayOf(date)];
  const month = MONTHS[lang][date.getMonth()];
  return lang === 'es' ? `${wd.toLowerCase()} ${date.getDate()} de ${month}` : `${wd}, ${month} ${date.getDate()}`;
}

/** "9:27" from an ISO date-time. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export interface DaySummary {
  total: number;
  done: number;
  couldNot: number;
  open: number;
  minutes: number;
}

export function summarize(dayTasks: { id: string; minutes: number }[], results: Record<string, { status: TaskResult['status'] }>): DaySummary {
  let done = 0;
  let couldNot = 0;
  for (const tk of dayTasks) {
    const r = results[tk.id];
    if (r?.status === 'done') done++;
    else if (r?.status === 'could_not') couldNot++;
  }
  return { total: dayTasks.length, done, couldNot, open: dayTasks.length - done - couldNot, minutes: sumMinutes(dayTasks) };
}

/** "staff:rosa" -> "Rosa". */
export function whoName(by: string): string {
  const name = by.split(':')[1] ?? by;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** The work days and hours of one crew (the house's are the household's own). */
export function scheduleFor(h: { workDays: Weekday[]; workStart: string; workEnd: string; garden: { workDays: Weekday[]; workStart: string; workEnd: string } }, crew: 'house' | 'garden') {
  return crew === 'garden' ? h.garden : { workDays: h.workDays, workStart: h.workStart, workEnd: h.workEnd };
}
